'use client';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { IoCloseSharp } from 'react-icons/io5';
import { Else, If, Then, When } from 'react-if';

import cn from '@/lib/clsxm';
import { useClientExtraLargeScreen, useClientSmallScreen } from '@/lib/user-agent/client';

import { SIZE_ENUM } from '@/components/@base/@helpers/types';
import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import { Text } from '@/components/@base/typography';
import XImage from '@/components/@base/x-image';
import PriceView from '@/components/shared/components/price-view';
import { useDelayedVisibilityOnFocus } from '@/components/shared/components/smart-offers/hooks/useDelayedVisibilityOnFocus';
import { useCloseSmartOffer } from '@/components/shared/components/smart-offers/services/hooks';
import useAddToCartHandler from '@/components/shared/helpers/hooks/use-add-to-cart-handler';

import { useSmartOfferStore } from '@/store/smart-offer.store';

import localization from '@/constant/localization';
import SmartOfferModel from '@/models/smart-offer.model';

export default function SmartSideIn({ data }: { data: SmartOfferModel }) {
  const [showSideIn, setShowSideIn] = useState(true);
  const [hasHidden, setHasHidden] = useState(false);
  const { showSmartOffer } = useSmartOfferStore();
  const isSmallScreen = useClientSmallScreen();
  const isExtraLarge = useClientExtraLargeScreen();
  const { handleAddToCart, isAddingToCart } = useAddToCartHandler();
  const { mutate, isPending } = useCloseSmartOffer();
  const delay = data?.displayDelay ?? 500;
  const isVisible = useDelayedVisibilityOnFocus(delay);
  const path = usePathname();
  const isProductPage = /^\/web\/[^/]+(\/(comments|support|reviews))?$/.test(path);
  const isCartPage = path === '/cart';

  useEffect(() => {
    if (isExtraLarge || !showSmartOffer || hasHidden) return;

    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollLimit = isProductPage ? 300 : 600;

      if (scrollY > scrollLimit) {
        setShowSideIn(false);
        setHasHidden(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isExtraLarge, showSmartOffer, hasHidden]);

  const handleAddToCartClick = () => {
    handleAddToCart({ data });
  };
  const handleClose = () => {
    setShowSideIn(false);
    mutate(data?.id, {
      onError: (e) => {},
      onSuccess: (res) => {},
    });
  };
  const previewLink =
    data?.productType === 'addon'
      ? `/web/${data?.product?.targetSlug}`
      : `/web/${data?.product?.slug}`;
  useEffect(() => {
    if (!showSmartOffer) {
      setShowSideIn(false);
    }
  }, [showSmartOffer]);
  return (
    <Container
      className={cn(
        'fixed bottom-[40px] z-60 h-auto rounded-md bg-[#FFFFFF] shadow-[0px_20px_60px_-15px_#00000026] transition-all duration-700 ease-in-out',

        !isSmallScreen ? 'w-[343px]!' : 'h-auto w-[95vw]!',

        showSideIn && isVisible
          ? 'right-3 translate-x-0 opacity-100'
          : // slide fully out via transform so it works for any box width (w-[95vw] on mobile);
            // a fixed `-right-[370px]` only cleared the old 343px desktop box
            'right-3 translate-x-[120%] opacity-100',

        isCartPage && 'bottom-[150px] md:bottom-[40px]',
      )}
    >
      <Container className='flex flex-col'>
        <XImage
          width={342}
          height={145}
          src={data?.product?.thumbnail?.publicLink}
          alt={data?.title}
          className='max-h-[145px] w-full rounded-[10px] object-cover object-top'
        />
        <Container className='flex h-full flex-col items-center justify-around'>
          <Container className='relative flex-col px-4 pt-4! pb-2!'>
            <Text
              semiBold
              size={SIZE_ENUM.XL}
              className='line-clamp-2 text-center leading-[38px]! text-[#424244]'
            >
              {data?.title}
            </Text>
            <Text className='line-clamp-2 text-start text-[13px] leading-[25px] text-[#76767C] md:line-clamp-3'>
              {data?.description}
            </Text>
          </Container>
          <When
            condition={
              data?.showDescription && data?.product?.price && data?.product?.price > 0
            }
          >
            <Container className='w-full'>
              <Container className='relative mr-6 ml-5 flex h-[52px] items-center justify-end rounded-[6px] bg-gradient-to-tr from-[rgba(255,218,162,0.4)] to-[rgba(255,243,224,0.4)] py-2 pr-[15px] pl-2.5 md:mr-[22px]! md:ml-8'>
                <PriceView
                  lastPriceClassName='text-start mr-0.5 -mb-2! text-[15px]! tracking-[-0.13em]'
                  discountLabelClassName=' text-[13px]! tracking-[-0.12em]! absolute right-[15px] top-[15%] mx-auto border-[#FFFFFF] border-[2px] w-9 h-9 shadow-[0px_4px_12px_-5px_#FF7B016E]'
                  showDiscountLabel={true}
                  key={data?.id}
                  price={data?.product?.price}
                  lastPrice={data?.product?.lastPrice}
                  discount={data?.product?.discount}
                  currencyClassName='text-[12px] text-[#BFBFBF]'
                  priceWrapperClassName='flex gap-[5px]'
                  priceClassName='text-[21px]! -ml-0.5 tracking-[-0.13em]'
                />
              </Container>
            </Container>
          </When>
          <Container
            className={clsx(
              'relative flex flex-row items-center pt-3 pb-4',
              // align the button row's outer edges with the orange price box above.
              // w-full + PADDING that matches the price box's margins (pr-6/pl-5 == its mr-6/ml-5),
              // not margins — margins on a w-full box overflow the card by their own width.
              // gap-2.5 (10px) + flex-1 buttons (below): fixed 10px gap, buttons fill the rest.
              data?.showDescription
                ? 'w-full gap-2.5 pr-6 pl-5 md:pr-[22px]! md:pl-8'
                : 'justify-between gap-2 mr-3 ml-3 pr-4 md:mx-0! md:gap-2.5 md:pl-4',
            )}
          >
            <If condition={data?.showDescription}>
              <Then>
                <a
                  href={previewLink}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='min-w-0 flex-1'
                >
                  <Button className='h-[52px] w-full bg-[#F4F5F6]! text-[15px] whitespace-nowrap! text-[#878F9B] transition-colors! duration-500 hover:border-1 hover:border-[#878F9B] hover:bg-[#878F9B]! hover:text-[#F4F5F6]'>
                    {localization.viewProduct}
                  </Button>
                </a>
              </Then>
              <Else>
                <When condition={data?.product?.price}>
                  <Container
                    className={clsx(
                      data?.product?.discount &&
                        data?.product?.discount > 0 &&
                        'mr-3! pr-2 md:mr-9!',
                      'relative w-fit',
                    )}
                  >
                    <PriceView
                      discountLabelClassName=' text-[13px]! absolute -right-10  top-[15%] mx-auto border-[#FFFFFF] border-[2px] w-9 h-9 shadow-[0px_4px_12px_-5px_#FF7B016E]'
                      showDiscountLabel={true}
                      key={data?.id}
                      price={data?.product?.price}
                      lastPrice={data?.product?.lastPrice}
                      discount={data?.product?.discount}
                      currencyClassName='text-[11px]'
                      lastPriceClassName='-mb-2!'
                    />
                  </Container>
                </When>
              </Else>
            </If>

            <Button
              className={clsx(
                'h-[52px] bg-[#FFB616]! text-[15px] transition-colors! duration-500 hover:border-1 hover:border-[#F89406] hover:bg-[#FFF]! hover:text-[#F89406]',
                data?.showDescription
                  ? 'min-w-0 flex-1'
                  : 'w-[125px] md:w-full md:min-w-[140px]',
              )}
              onClick={handleAddToCartClick}
              isLoading={isAddingToCart}
            >
              {data?.buttonText}
            </Button>
          </Container>
        </Container>
      </Container>
      <Container
        className='absolute -top-[35px] right-0 h-8 w-8 cursor-pointer rounded-md bg-[#76767C] pt-1! pr-1! pb-1! pl-1.5! text-[#fff] ring ring-white transition duration-300 hover:bg-[#424244] md:-top-[35px]'
        onClick={handleClose}
      >
        <IoCloseSharp fill='white' size={23} />
      </Container>
    </Container>
  );
}
